import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import AdminLayout from "@/components/layout/AdminLayout";
import API, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { CheckCircle2, ChevronDown, Clock3, Download, RefreshCcw, Save, Search, Truck } from "lucide-react";

const badgeClassMap = {
  not_sent: "bg-stone-100 text-stone-700",
  sent_to_central: "bg-blue-100 text-blue-700",
  received_from_central: "bg-emerald-100 text-emerald-700",
};

const badgeLabelMap = {
  not_sent: "Belum Dikirim",
  sent_to_central: "Sudah Dikirim ke Pusat",
  received_from_central: "Sudah Datang dari Pusat",
};

const statusFilterOptions = [
  { value: "all", label: "Semua" },
  { value: "not_sent", label: "Belum Dikirim" },
  { value: "sent_to_central", label: "Sudah Dikirim" },
  { value: "received_from_central", label: "Sudah Datang" },
];

const CHECKLIST_PREFIX = {
  order: "order",
  received: "received",
};

function buildChecklistKey(productId, section, rawSignature) {
  return `${productId}-${CHECKLIST_PREFIX[section]}-${rawSignature}`;
}

function buildChecklistStateFromItems(items = []) {
  const next = { order: {}, received: {} };
  items.forEach((item) => {
    const signatures = item.ordered_variants || [];
    const orderMap = item.monitoring?.order_checklist || {};
    const receivedMap = item.monitoring?.received_checklist || {};

    signatures.forEach((variantItem) => {
      const rawSignature = variantItem.signature;
      next.order[buildChecklistKey(item.product_id, "order", rawSignature)] = !!orderMap[rawSignature];
      next.received[buildChecklistKey(item.product_id, "received", rawSignature)] = !!receivedMap[rawSignature];
    });
  });
  return next;
}

function VariantChecklistAccordion({
  title,
  items = [],
  emptyLabel = "Belum ada varian terorder.",
  checkedMap = {},
  onToggle = () => {},
  defaultOpen = false,
}) {
  return (
    <details defaultOpen={defaultOpen} className="group rounded-lg border border-stone-200 bg-white/90">
      <summary className="flex cursor-pointer list-none items-center justify-between gap-3 px-3 py-2 text-[11px] font-semibold uppercase tracking-wider text-stone-600">
        <div className="flex items-center gap-2">
          <span>{title}</span>
          <span className="inline-flex min-w-6 justify-center rounded-full bg-stone-100 px-1.5 py-0.5 text-[10px] text-stone-600">
            {items.length}
          </span>
        </div>
        <ChevronDown className="h-4 w-4 text-stone-400 transition-transform group-open:rotate-180" />
      </summary>
      <div className="border-t border-stone-100 px-3 py-2">
        {items.length ? (
          <div className="space-y-2">
            {items.map((variantItem) => (
              <label key={variantItem.key} className="flex cursor-pointer items-start gap-2 rounded-lg border border-stone-100 bg-stone-50/70 px-3 py-2">
                <input
                  type="checkbox"
                  checked={!!checkedMap[variantItem.key]}
                  onChange={(e) => onToggle(variantItem.key, e.target.checked)}
                  className="mt-0.5 rounded border-stone-300 text-[#8A9A86] focus:ring-[#8A9A86]"
                />
                <div className="min-w-0 flex-1">
                  <p className="font-medium normal-case tracking-normal text-stone-800 break-words">{variantItem.label || "Tanpa varian"}</p>
                  <div className="mt-1 flex flex-wrap gap-2 text-[11px] normal-case tracking-normal text-stone-500">
                    <span>Qty: <span className="font-semibold text-stone-800">{variantItem.quantity || 0}</span></span>
                  </div>
                </div>
              </label>
            ))}
          </div>
        ) : (
          <p className="text-[11px] text-stone-400">{emptyLabel}</p>
        )}
      </div>
    </details>
  );
}

export default function AdminPOMonitoring() {
  const [items, setItems] = useState([]);
  const [changedData, setChangedData] = useState({});
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState("");
  const [downloadingPdfId, setDownloadingPdfId] = useState("");
  const [checklistState, setChecklistState] = useState({ order: {}, received: {} });
  const [initialChecklistState, setInitialChecklistState] = useState({ order: {}, received: {} });
  const autosaveTimersRef = useRef({});

  const fetchItems = useCallback(async () => {
    setLoading(true);
    try {
      const response = await API.get("/admin/po-monitoring");
      const nextItems = response.data?.items || [];
      const nextChecklistState = buildChecklistStateFromItems(nextItems);
      setItems(nextItems);
      setChangedData({});
      setChecklistState(nextChecklistState);
      setInitialChecklistState(nextChecklistState);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchItems();
  }, [fetchItems]);

  const getMonitoringValue = useCallback((item, field, fallback = "") => {
    const rowDraft = changedData[item.product_id] || {};
    if (Object.prototype.hasOwnProperty.call(rowDraft, field)) return rowDraft[field];
    return item.monitoring?.[field] ?? fallback;
  }, [changedData]);

  const getDraftStatus = useCallback((item) => {
    if (getMonitoringValue(item, "received_from_central", false)) return "received_from_central";
    if (getMonitoringValue(item, "sent_to_central", false)) return "sent_to_central";
    return "not_sent";
  }, [getMonitoringValue]);

  const filteredItems = useMemo(() => {
    const keyword = search.trim().toLowerCase();
    return items.filter((item) => {
      const matchesSearch = !keyword || [item.product_name, item.brand_name, item.preorder_closing_date, item.preorder_delivery_estimate]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(keyword));
      const matchesStatus = statusFilter === "all" || getDraftStatus(item) === statusFilter;
      return matchesSearch && matchesStatus;
    });
  }, [items, search, statusFilter, getDraftStatus]);

  const statusCounts = useMemo(() => ({
    all: items.length,
    not_sent: items.filter((item) => getDraftStatus(item) === "not_sent").length,
    sent_to_central: items.filter((item) => getDraftStatus(item) === "sent_to_central").length,
    received_from_central: items.filter((item) => getDraftStatus(item) === "received_from_central").length,
  }), [items, getDraftStatus]);

  const buildChecklistItems = useCallback((item, section) => (
    (item.ordered_variants || []).map((variantItem) => ({
      ...variantItem,
      key: buildChecklistKey(item.product_id, section, variantItem.signature),
      rawSignature: variantItem.signature,
    }))
  ), []);

  const getChecklistPayloadForItem = useCallback((item, section, sourceState) => {
    const payload = {};
    buildChecklistItems(item, section).forEach((variantItem) => {
      payload[variantItem.rawSignature] = !!sourceState[section]?.[variantItem.key];
    });
    return payload;
  }, [buildChecklistItems]);

  const hasChecklistChanges = useCallback((item) => {
    const currentOrder = JSON.stringify(getChecklistPayloadForItem(item, "order", checklistState));
    const initialOrder = JSON.stringify(getChecklistPayloadForItem(item, "order", initialChecklistState));
    const currentReceived = JSON.stringify(getChecklistPayloadForItem(item, "received", checklistState));
    const initialReceived = JSON.stringify(getChecklistPayloadForItem(item, "received", initialChecklistState));
    return currentOrder !== initialOrder || currentReceived !== initialReceived;
  }, [checklistState, initialChecklistState, getChecklistPayloadForItem]);

  const getChecklistProgress = useCallback((item, section) => {
    const checklistItems = buildChecklistItems(item, section);
    const total = checklistItems.length;
    const checked = checklistItems.filter((variantItem) => !!checklistState[section]?.[variantItem.key]).length;
    const percent = total ? Math.round((checked / total) * 100) : 0;
    return { checked, total, percent };
  }, [buildChecklistItems, checklistState]);

  const clearAutosaveTimer = useCallback((productId) => {
    if (autosaveTimersRef.current[productId]) {
      clearTimeout(autosaveTimersRef.current[productId]);
      delete autosaveTimersRef.current[productId];
    }
  }, []);

  useEffect(() => {
    return () => {
      Object.values(autosaveTimersRef.current).forEach((timerId) => clearTimeout(timerId));
      autosaveTimersRef.current = {};
    };
  }, []);

  const syncChecklistStateForSavedItem = useCallback((savedItem) => {
    const orderPayload = savedItem.monitoring?.order_checklist || {};
    const receivedPayload = savedItem.monitoring?.received_checklist || {};
    const variantItems = savedItem.ordered_variants || [];

    setChecklistState((prev) => {
      const next = {
        order: { ...(prev.order || {}) },
        received: { ...(prev.received || {}) },
      };
      Object.keys(next.order).forEach((key) => {
        if (key.startsWith(`${savedItem.product_id}-${CHECKLIST_PREFIX.order}-`)) delete next.order[key];
      });
      Object.keys(next.received).forEach((key) => {
        if (key.startsWith(`${savedItem.product_id}-${CHECKLIST_PREFIX.received}-`)) delete next.received[key];
      });
      variantItems.forEach((variantItem) => {
        next.order[buildChecklistKey(savedItem.product_id, "order", variantItem.signature)] = !!orderPayload[variantItem.signature];
        next.received[buildChecklistKey(savedItem.product_id, "received", variantItem.signature)] = !!receivedPayload[variantItem.signature];
      });
      return next;
    });

    setInitialChecklistState((prev) => {
      const next = {
        order: { ...(prev.order || {}) },
        received: { ...(prev.received || {}) },
      };
      Object.keys(next.order).forEach((key) => {
        if (key.startsWith(`${savedItem.product_id}-${CHECKLIST_PREFIX.order}-`)) delete next.order[key];
      });
      Object.keys(next.received).forEach((key) => {
        if (key.startsWith(`${savedItem.product_id}-${CHECKLIST_PREFIX.received}-`)) delete next.received[key];
      });
      variantItems.forEach((variantItem) => {
        next.order[buildChecklistKey(savedItem.product_id, "order", variantItem.signature)] = !!orderPayload[variantItem.signature];
        next.received[buildChecklistKey(savedItem.product_id, "received", variantItem.signature)] = !!receivedPayload[variantItem.signature];
      });
      return next;
    });
  }, []);

  const persistMonitoringItem = useCallback(async (item, options = {}) => {
    const { silentSuccess = false, sourceChecklistState = checklistState } = options;
    const sentToCentral = !!getMonitoringValue(item, "sent_to_central", false);
    const receivedFromCentral = !!getMonitoringValue(item, "received_from_central", false);

    if (receivedFromCentral && !sentToCentral) {
      if (!silentSuccess) {
        toast.error("Checklist datang dari pusat hanya bisa diisi setelah kirim ke pusat dicentang.");
      }
      return false;
    }

    clearAutosaveTimer(item.product_id);
    setSavingId(item.product_id);
    try {
      const response = await API.put(`/admin/po-monitoring/${item.product_id}`, {
        sent_to_central: sentToCentral,
        sent_to_central_at: getMonitoringValue(item, "sent_to_central_at", "") || null,
        sent_to_central_note: getMonitoringValue(item, "sent_to_central_note", ""),
        received_from_central: receivedFromCentral,
        received_from_central_at: getMonitoringValue(item, "received_from_central_at", "") || null,
        received_from_central_note: getMonitoringValue(item, "received_from_central_note", ""),
        order_checklist: getChecklistPayloadForItem(item, "order", sourceChecklistState),
        received_checklist: getChecklistPayloadForItem(item, "received", sourceChecklistState),
      });

      const updatedItem = response.data?.item;
      if (updatedItem) {
        setItems((prev) => prev.map((row) => row.product_id === item.product_id ? updatedItem : row));
        setChangedData((prev) => {
          const next = { ...prev };
          delete next[item.product_id];
          return next;
        });
        syncChecklistStateForSavedItem(updatedItem);
      }
      if (!silentSuccess) {
        toast.success("Monitoring PO berhasil disimpan");
      }
      return true;
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
      return false;
    } finally {
      setSavingId((prev) => prev === item.product_id ? "" : prev);
    }
  }, [checklistState, clearAutosaveTimer, getChecklistPayloadForItem, getMonitoringValue, syncChecklistStateForSavedItem]);

  const scheduleChecklistAutosave = useCallback((item, nextChecklistState) => {
    clearAutosaveTimer(item.product_id);
    autosaveTimersRef.current[item.product_id] = setTimeout(() => {
      persistMonitoringItem(item, { silentSuccess: true, sourceChecklistState: nextChecklistState });
    }, 700);
  }, [clearAutosaveTimer, persistMonitoringItem]);

  const handleChecklistToggle = (item, section, key, checked) => {
    const nextChecklistState = {
      ...checklistState,
      [section]: {
        ...(checklistState[section] || {}),
        [key]: checked,
      },
    };
    setChecklistState(nextChecklistState);
    scheduleChecklistAutosave(item, nextChecklistState);
  };

  const handleFieldChange = (productId, field, value) => {
    setChangedData((prev) => ({
      ...prev,
      [productId]: {
        ...(prev[productId] || {}),
        [field]: value,
      },
    }));
  };

  const handleToggleSent = (item, checked) => {
    handleFieldChange(item.product_id, "sent_to_central", checked);
    if (!checked) {
      handleFieldChange(item.product_id, "sent_to_central_at", "");
      handleFieldChange(item.product_id, "sent_to_central_note", "");
      handleFieldChange(item.product_id, "received_from_central", false);
      handleFieldChange(item.product_id, "received_from_central_at", "");
      handleFieldChange(item.product_id, "received_from_central_note", "");
    }
  };

  const handleToggleReceived = (item, checked) => {
    handleFieldChange(item.product_id, "received_from_central", checked);
    if (!checked) {
      handleFieldChange(item.product_id, "received_from_central_at", "");
      handleFieldChange(item.product_id, "received_from_central_note", "");
    }
  };

  const handleSave = async (item) => {
    await persistMonitoringItem(item, { silentSuccess: false, sourceChecklistState: checklistState });
  };

  const handleDownloadPdf = useCallback(async (item) => {
    setDownloadingPdfId(item.product_id);
    try {
      const params = new URLSearchParams();
      params.set("product_id", item.product_id);
      const response = await API.get(`/admin/po-monitoring/purchase-order-pdf?${params.toString()}`, { responseType: "blob" });
      const blobUrl = window.URL.createObjectURL(new Blob([response.data], { type: "application/pdf" }));
      const link = document.createElement("a");
      link.href = blobUrl;
      const disposition = response.headers?.["content-disposition"] || "";
      const filenameMatch = disposition.match(/filename=([^;]+)/i);
      const filename = filenameMatch?.[1]?.replace(/['"]/g, "") || `purchase_order_${item.product_id}.pdf`;
      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(blobUrl);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Gagal download PDF purchase order");
    } finally {
      setDownloadingPdfId((prev) => (prev === item.product_id ? "" : prev));
    }
  }, []);

  return (
    <AdminLayout title="Monitoring PO">
      <div className="space-y-6" data-testid="admin-po-monitoring-page">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div>
            <p className="text-sm text-stone-500">Catatan internal bulk per produk preorder, terpisah dari workflow utama customer.</p>
            <p className="text-xs text-stone-400 mt-1">Fokus v1 hanya untuk checkpoint kirim ke pusat dan barang datang dari pusat.</p>
          </div>
          <div className="flex flex-col sm:flex-row gap-3 w-full lg:w-auto">
            <div className="relative w-full sm:w-72">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Cari produk / brand / tanggal..."
                className="w-full h-10 pl-9 pr-3 rounded-lg border border-stone-200 bg-white text-sm outline-none focus:border-[#8A9A86]"
              />
            </div>
            <button
              onClick={fetchItems}
              className="h-10 px-4 border border-stone-200 rounded-lg text-stone-600 hover:bg-stone-50 transition-colors inline-flex items-center justify-center gap-2"
            >
              <RefreshCcw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
              Refresh
            </button>
          </div>
        </div>

        <div className="flex flex-wrap gap-2">
          {statusFilterOptions.map((option) => {
            const active = statusFilter === option.value;
            return (
              <button
                key={option.value}
                onClick={() => setStatusFilter(option.value)}
                className={`inline-flex items-center gap-2 px-3 py-2 rounded-full border text-sm font-medium transition-colors ${active ? "border-[#8A9A86] bg-[#8A9A86] text-white" : "border-stone-200 bg-white text-stone-600 hover:bg-stone-50"}`}
              >
                <span>{option.label}</span>
                <span className={`inline-flex min-w-6 justify-center rounded-full px-1.5 py-0.5 text-xs ${active ? "bg-white/20 text-white" : "bg-stone-100 text-stone-600"}`}>
                  {statusCounts[option.value] || 0}
                </span>
              </button>
            );
          })}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div className="bg-white border border-stone-200 rounded-xl p-4">
            <p className="text-xs font-semibold uppercase tracking-wider text-stone-500">Total Produk PO</p>
            <p className="text-2xl font-serif text-stone-900 mt-2">{items.length}</p>
          </div>
          <div className="bg-white border border-stone-200 rounded-xl p-4">
            <p className="text-xs font-semibold uppercase tracking-wider text-stone-500">Sudah Dikirim ke Pusat</p>
            <p className="text-2xl font-serif text-blue-700 mt-2">{items.filter((item) => item.monitoring?.sent_to_central).length}</p>
          </div>
          <div className="bg-white border border-stone-200 rounded-xl p-4">
            <p className="text-xs font-semibold uppercase tracking-wider text-stone-500">Sudah Datang dari Pusat</p>
            <p className="text-2xl font-serif text-emerald-700 mt-2">{items.filter((item) => item.monitoring?.received_from_central).length}</p>
          </div>
        </div>

        <div className="flex items-center justify-between gap-3 text-sm text-stone-500">
          <p>
            Menampilkan <span className="font-semibold text-stone-800">{filteredItems.length}</span> dari <span className="font-semibold text-stone-800">{items.length}</span> produk PO
          </p>
          {statusFilter !== "all" && (
            <button onClick={() => setStatusFilter("all")} className="text-[#8A9A86] font-medium hover:underline">
              Reset filter
            </button>
          )}
        </div>

        <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
          {loading ? (
            <div className="xl:col-span-2 bg-white border border-stone-200 rounded-xl p-10 text-center text-stone-400">Loading...</div>
          ) : filteredItems.length === 0 ? (
            <div className="xl:col-span-2 bg-white border border-stone-200 rounded-xl p-10 text-center text-stone-400">Belum ada produk preorder yang cocok.</div>
          ) : filteredItems.map((item) => {
            const rowChanged = !!changedData[item.product_id] || hasChecklistChanges(item);
            const sentToCentral = !!getMonitoringValue(item, "sent_to_central", false);
            const receivedFromCentral = !!getMonitoringValue(item, "received_from_central", false);
            const statusKey = receivedFromCentral ? "received_from_central" : sentToCentral ? "sent_to_central" : "not_sent";

            return (
              <div key={item.product_id} className="bg-white border border-stone-200 rounded-xl p-4 space-y-4 shadow-sm">
                <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
                  <div className="space-y-2">
                    <p className="font-semibold text-stone-900">{item.product_name}</p>
                    <p className="text-xs text-stone-500">{item.brand_name || "No Brand"}</p>
                    <span className={`inline-flex px-2.5 py-1 rounded-full text-[11px] font-semibold ${badgeClassMap[statusKey]}`}>
                      {badgeLabelMap[statusKey]}
                    </span>
                  </div>
                  <div className="space-y-2 text-xs text-stone-600 sm:text-right">
                    <div className="flex items-center gap-2 sm:justify-end">
                      <Truck className="w-3.5 h-3.5 text-stone-400" />
                      <span>Total Order: <span className="font-semibold text-stone-900">{item.total_orders || 0}</span></span>
                    </div>
                    <div className="flex items-center gap-2 sm:justify-end">
                      <Clock3 className="w-3.5 h-3.5 text-stone-400" />
                      <span>Close PO: <span className="font-semibold text-stone-900">{item.preorder_closing_date || "-"}</span></span>
                    </div>
                    <div className="flex items-center gap-2 sm:justify-end">
                      <CheckCircle2 className="w-3.5 h-3.5 text-stone-400" />
                      <span>Estimasi: <span className="font-semibold text-stone-900">{item.preorder_delivery_estimate || "-"}</span></span>
                    </div>
                  </div>
                </div>

                <div className="space-y-3">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {[
                      { key: "order", label: "Progress Varian Order", tone: "bg-blue-500" },
                      { key: "received", label: "Progress Barang Datang", tone: "bg-emerald-500" },
                    ].map((progressItem) => {
                      const progress = getChecklistProgress(item, progressItem.key);
                      return (
                        <div key={progressItem.key} className="rounded-lg border border-stone-200 bg-stone-50/80 p-3">
                          <div className="flex items-center justify-between gap-2 text-xs">
                            <p className="font-semibold text-stone-700">{progressItem.label}</p>
                            <span className="font-semibold text-stone-900">{progress.checked}/{progress.total}</span>
                          </div>
                          <div className="mt-2 h-2 overflow-hidden rounded-full bg-stone-200">
                            <div className={`h-full rounded-full transition-all ${progressItem.tone}`} style={{ width: `${progress.percent}%` }} />
                          </div>
                          <p className="mt-2 text-[11px] text-stone-500">{progress.total ? `${progress.percent}% varian sudah dicek` : "Belum ada varian"}</p>
                        </div>
                      );
                    })}
                  </div>

                  <VariantChecklistAccordion
                    title="Checklist Varian Order"
                    items={buildChecklistItems(item, "order")}
                    checkedMap={checklistState.order}
                    onToggle={(key, checked) => handleChecklistToggle(item, "order", key, checked)}
                    defaultOpen
                  />
                  <VariantChecklistAccordion
                    title="Checklist Barang Datang"
                    items={buildChecklistItems(item, "received")}
                    checkedMap={checklistState.received}
                    onToggle={(key, checked) => handleChecklistToggle(item, "received", key, checked)}
                    emptyLabel="Belum ada checklist barang datang."
                  />
                  <p className="text-[11px] text-stone-400">Checklist akan tersimpan otomatis beberapa saat setelah dicentang.</p>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                  <div className="space-y-3 rounded-lg border border-stone-100 bg-stone-50/60 p-3">
                    <label className="flex items-center gap-2 text-sm font-medium text-stone-700">
                      <input
                        type="checkbox"
                        checked={sentToCentral}
                        onChange={(e) => handleToggleSent(item, e.target.checked)}
                        className="rounded border-stone-300"
                      />
                      Sudah dikirim ke pusat
                    </label>
                    <input
                      type="date"
                      value={getMonitoringValue(item, "sent_to_central_at", "") || ""}
                      onChange={(e) => handleFieldChange(item.product_id, "sent_to_central_at", e.target.value)}
                      disabled={!sentToCentral}
                      className="w-full h-10 rounded-lg border border-stone-200 px-3 text-sm outline-none focus:border-[#8A9A86] disabled:bg-stone-50 disabled:text-stone-400"
                    />
                    <textarea
                      rows={2}
                      value={getMonitoringValue(item, "sent_to_central_note", "")}
                      onChange={(e) => handleFieldChange(item.product_id, "sent_to_central_note", e.target.value)}
                      disabled={!sentToCentral}
                      placeholder="Catatan kirim ke pusat"
                      className="w-full rounded-lg border border-stone-200 px-3 py-2 text-sm outline-none focus:border-[#8A9A86] disabled:bg-stone-50 disabled:text-stone-400"
                    />
                    <p className="text-[11px] text-stone-400">Update terakhir: {item.monitoring?.sent_to_central_by || "-"}</p>
                  </div>

                  <div className="space-y-3 rounded-lg border border-stone-100 bg-stone-50/60 p-3">
                    <label className={`flex items-center gap-2 text-sm font-medium ${sentToCentral ? "text-stone-700" : "text-stone-400"}`}>
                      <input
                        type="checkbox"
                        checked={receivedFromCentral}
                        onChange={(e) => handleToggleReceived(item, e.target.checked)}
                        disabled={!sentToCentral}
                        className="rounded border-stone-300"
                      />
                      Barang sudah datang dari pusat
                    </label>
                    <input
                      type="date"
                      value={getMonitoringValue(item, "received_from_central_at", "") || ""}
                      onChange={(e) => handleFieldChange(item.product_id, "received_from_central_at", e.target.value)}
                      disabled={!receivedFromCentral}
                      className="w-full h-10 rounded-lg border border-stone-200 px-3 text-sm outline-none focus:border-[#8A9A86] disabled:bg-stone-50 disabled:text-stone-400"
                    />
                    <textarea
                      rows={2}
                      value={getMonitoringValue(item, "received_from_central_note", "")}
                      onChange={(e) => handleFieldChange(item.product_id, "received_from_central_note", e.target.value)}
                      disabled={!receivedFromCentral}
                      placeholder="Catatan barang datang"
                      className="w-full rounded-lg border border-stone-200 px-3 py-2 text-sm outline-none focus:border-[#8A9A86] disabled:bg-stone-50 disabled:text-stone-400"
                    />
                    <p className="text-[11px] text-stone-400">Update terakhir: {item.monitoring?.received_from_central_by || "-"}</p>
                  </div>
                </div>

                <div className="space-y-2">
                  <p className="text-[11px] text-stone-400">
                    {rowChanged ? "Ada perubahan yang belum disimpan" : "Belum ada perubahan untuk disimpan"}
                  </p>
                  <button
                    onClick={() => handleDownloadPdf(item)}
                    disabled={downloadingPdfId === item.product_id}
                    className="w-full inline-flex items-center justify-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold border border-[#8A9A86] text-[#6F7F6B] hover:bg-[#F4F6F2] transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    <Download className={`w-4 h-4 ${downloadingPdfId === item.product_id ? "animate-pulse" : ""}`} />
                    {downloadingPdfId === item.product_id ? "Menyiapkan PDF..." : "Download PO PDF"}
                  </button>
                  <button
                    onClick={() => handleSave(item)}
                    disabled={!rowChanged || savingId === item.product_id}
                    className={`w-full inline-flex items-center justify-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold transition-colors ${rowChanged && savingId !== item.product_id ? "bg-[#8A9A86] text-white hover:bg-[#768573]" : "bg-stone-200 text-stone-500 border border-stone-300 cursor-not-allowed"}`}
                  >
                    <Save className="w-4 h-4" />
                    {savingId === item.product_id ? "Menyimpan..." : "Simpan Perubahan"}
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </AdminLayout>
  );
}
